import React from 'react'
import { Field, reduxForm } from 'redux-form'
import styles from './styles.css'
import { renderSelectField, renderInputField } from 'components/FormFields'
import { Button } from 'components/Button'
import states from 'lib/states'
import { STATUS_OPTIONS } from 'lib/constants'
import formatters from 'lib/formatters'
import { filterEmptyValues } from './subaccountUtils'

type Props = {
  handleSubmit: Function,
  submitting: boolean,
  pristine: boolean,
  reset: Function,
  dispatch: Function,
  onCancel: Function,
  initialValues: Object,
  subaccount: Object,
  accountNumber: string,
  subnumber: string,
  onSuccess: Function,
  onSubmit?: Function,
}

const HomeForm = (props: Props) => {
  const { handleSubmit, submitting, pristine, onCancel, subaccount } = props

  const services = subaccount?.asset?.services || []

  const inspectionService = services.find((service) => service.service_type === 'home_inspection')
  const taxService = services.find((service) => service.service_type === 'tax')
  const appraisalService = services.find((service) => service.service_type === 'appraisal')

  const inspectionEvents = inspectionService?.events || []
  const taxEvents = taxService?.events || []
  const appraisalEvents = appraisalService?.events || []

  const onSubmit = (values) => {
    const filteredValues = filterEmptyValues(values)
    return props.onSubmit ? props.onSubmit(filteredValues) : Promise.resolve()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.contentContainer}>
        <div className={styles.formRow}>
          <div className={styles.formSection}>
            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Home Details</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field name="street_1" type="text" component={renderInputField} label="" placeholder="Street Address" />
                <Field name="street_2" type="text" component={renderInputField} label="" placeholder="Street Address" />
                <div className={styles.formRow}>
                  <div className={styles.formSection} style={{ flex: '3' }}>
                    <Field name="city" type="text" component={renderInputField} label="" placeholder="City" />
                  </div>
                  <div className={styles.formSection} style={{ flex: '1' }}>
                    <Field name="state" component={renderSelectField} options={states} placeholderText="State" />
                  </div>
                </div>
                <div style={{ width: '50%' }}>
                  <Field name="zip" type="text" component={renderInputField} label="" placeholder="ZIP Code" />
                </div>
                <Field name="purchase_date" component={renderInputField} type="date" label="Purchase Date" placeholder="YYYY-MM-DD" />
              </div>
            </section>

            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Home Insurance</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field
                  name="home_insurance_provider"
                  type="text"
                  component={renderInputField}
                  label="Home Insurance Company"
                  placeholder="Company Name"
                />
                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <Field
                      name="home_insurance_coverage_start"
                      component={renderInputField}
                      type="date"
                      label="Coverage Begins"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <div className={styles.formSection}>
                    <Field
                      name="home_insurance_coverage_end"
                      component={renderInputField}
                      type="date"
                      label="Coverage Ends"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Inspection</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field
                  name="home_inspection_provider"
                  type="text"
                  component={renderInputField}
                  label="Inspection Company"
                  placeholder="Company Name"
                />
                <Field name="home_inspection_date" component={renderInputField} type="date" label="Inspection Date" placeholder="YYYY-MM-DD" />
                <div className={styles.inspectionLogTable}>
                  <h6>Inspection Log</h6>
                  {inspectionEvents.length > 0 ? (
                    inspectionEvents.map((event, index) => (
                      <div key={`inspection-${index}`} className={styles.logEntry}>
                        <div className={styles.logEntryRow}>
                          <span className={styles.logDate}>{formatters.date(event.date)}</span>
                          <span className={styles.logCompany}>{event.description || inspectionService.provider || '-'}</span>
                          <span className={styles.logValue}></span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.logEntry}>
                      <div className={styles.logEntryRow}>
                        <span className={styles.logDate}></span>
                        <span className={styles.logCompany}>No inspection data available</span>
                        <span className={styles.logValue}></span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>

          <div className={styles.formSection}>
            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>SubAccount Status</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field name="status" component={renderSelectField} label="Current Status" options={STATUS_OPTIONS} placeholder="Active" />
              </div>
            </section>

            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Property Taxes</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <Field name="tax_amount" type="text" component={renderInputField} label="Amount" placeholder="ex: 500.00" />
                  </div>
                  <div className={styles.formSection}>
                    <Field name="tax_date" component={renderInputField} type="date" label="Date Paid" placeholder="YYYY-MM-DD" />
                  </div>
                </div>
                <div className={styles.taxesLogTable}>
                  <h6>Taxes Log</h6>
                  {taxEvents.length > 0 ? (
                    taxEvents.map((event, index) => (
                      <div key={`tax-${index}`} className={styles.logEntry}>
                        <div className={styles.logEntryRow}>
                          <span className={styles.logDate}>{formatters.date(event.date)}</span>
                          <span className={styles.logCompany}>{formatters.money(event.amount) || '-'}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.logEntry}>
                      <div className={styles.logEntryRow}>
                        <span className={styles.logDate}></span>
                        <span className={styles.logCompany}>No tax data available</span>
                        <span className={styles.logValue}></span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Appraisal</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field name="appraisal_provider" type="text" component={renderInputField} label="Appraisal Company" placeholder="Company Name" />
                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <Field name="appraisal_amount" type="text" component={renderInputField} label="Amount" placeholder="ex: 150000.00" />
                  </div>
                  <div className={styles.formSection}>
                    <Field name="appraisal_date" component={renderInputField} type="date" label="Appraisal Date" placeholder="YYYY-MM-DD" />
                  </div>
                </div>
                <div className={styles.appraisalLogTable}>
                  <h6>Appraisal Log</h6>
                  {appraisalEvents.length > 0 ? (
                    appraisalEvents.map((event, index) => (
                      <div key={`appraisal-${index}`} className={styles.logEntry}>
                        <div className={styles.logEntryRow}>
                          <span className={styles.logDate}>{formatters.date(event.date)}</span>
                          <span className={styles.logCompany}>{event.description || '-'}</span>

                          <span className={styles.logCompany}>{formatters.money(event.amount) || '-'}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.logEntry}>
                      <div className={styles.logEntryRow}>
                        <span className={styles.logDate}></span>
                        <span className={styles.logCompany}>No appraisal data available</span>
                        <span className={styles.logValue}></span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section data-active={true} className={styles.collapseRef}>
              <div className={styles.titleSection}>
                <div className={styles.title}>
                  <h5>Flood Insurance</h5>
                </div>
              </div>
              <div className={styles.docsWrapper}>
                <Field
                  name="flood_insurance_provider"
                  type="text"
                  component={renderInputField}
                  label="Flood Insurance Company"
                  placeholder="Company Name"
                />
                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <Field
                      name="flood_insurance_coverage_start"
                      component={renderInputField}
                      type="date"
                      label="Coverage Begins"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <div className={styles.formSection}>
                    <Field
                      name="flood_insurance_coverage_end"
                      component={renderInputField}
                      type="date"
                      label="Coverage Ends"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <Button size="default" color="red" label="Cancel" onClick={onCancel} type="button" disabled={submitting} />
        <Button size="default" color="blue" label="Save Changes" type="submit" disabled={pristine || submitting} />
      </div>
    </form>
  )
}

export default reduxForm({
  form: 'homeSubaccountForm',
  enableReinitialize: true,
})(HomeForm)
